import { useCallback, useState, useRef } from "react";

export function useRemainingHeight(offset = 0) {
    const [height, setHeight] = useState(0);
    const [isLoading, setIsLoading] = useState(true);
    const nodeRef = useRef<HTMLElement | null>(null);

    const updateHeight = useCallback(() => {
        if (!nodeRef.current) return;

        // Use requestAnimationFrame to ensure DOM is ready
        requestAnimationFrame(() => {
            const rect = nodeRef.current!.getBoundingClientRect();
            const remainingHeight = window.innerHeight - rect.top - offset;
            setHeight(Math.max(0, remainingHeight));
            setIsLoading(false);
        });
    }, [offset]);

    const containerRef = useCallback((node: HTMLElement | null) => {
        // Clean up previous observers if node changes
        if (nodeRef.current) {
            window.removeEventListener('resize', updateHeight);
            // Note: observers are cleaned up in the return function below
        }

        nodeRef.current = node;
        
        if (!node) return;

        // Initial calculation
        updateHeight();

        // Listen for resize events
        window.addEventListener('resize', updateHeight);
        
        // Observe parent changes
        const resizeObserver = new ResizeObserver(updateHeight);
        const mutationObserver = new MutationObserver(updateHeight);
        
        // Observe the node and its parents for size changes
        resizeObserver.observe(node);
        if (node.parentElement) {
            resizeObserver.observe(node.parentElement);
            mutationObserver.observe(node.parentElement, {
                childList: true,
                subtree: true,
                attributes: true,
                attributeFilter: ['style', 'class']
            });
        }

        // Cleanup function
        return () => {
            window.removeEventListener('resize', updateHeight);
            resizeObserver.disconnect();
            mutationObserver.disconnect();
        };
    }, [updateHeight]);

    return { 
        height, 
        isLoading, 
        getHeight: containerRef,
        recalculate: updateHeight 
    };
}